<script setup>
import { computed, ref, useTemplateRef } from 'vue';
import { useStore } from 'vuex';


import DsIconButton from 'JIX/components/ds/DsIconButton.vue';

import SvgIcon from 'JIX/components/SvgIcon.vue';
import JobsearchFilterGroupFilters from './JobsearchFilterGroupFilters.vue';
import JobsearchFilterGroup from './JobsearchFilterGroup.vue';

import { __ } from 'JIX/gettext.js';
import JobsearchSearchBarGeneral from './JobsearchSearchBarGeneral.vue';
import JobsearchSearchMobile from './JobsearchSearchMobile.vue';

import { useSearchButtonText, useTwoWayBinding } from 'JIX/jobsearch/utils.js';
import { isMobile, isMedium, isLarge } from 'JIX/utils.js';
import JobsearchRenderQuery from './JobsearchRenderQuery.vue';

import 'icons/category.svg';
import 'icons/filter.svg';
import 'icons/close.svg';
import 'icons/service-jobsearch.svg';

const store = useStore();

const jobsearchbar = ref();
const allFilters = ref();
const userInput = useTwoWayBinding('userInput');
const geoFilter = useTemplateRef('geoFilter');
const allFiltersComponent = useTemplateRef('allFiltersComponent');

const workingQueryHitcount = computed(() => store.state.workingQueryHitcount);
const jobcountText = useSearchButtonText({ hitcount: workingQueryHitcount });

const numberOfActiveGeoFilters = computed(() => {
    return (
        (store.getters.workingQuery.address && store.getters.workingQuery.radius ? 1 : 0) +
        store.getters.workingQuery.geoareaid.length
    );
});

const numberOfActiveCategoryFilters = computed(() => {
    return (
        store.getters.workingQuery.subid.length
    );
});

const numberOfActiveFilters = computed(() => {
    return (
        store.getters.workingQuery.employment_type.length +
        store.getters.workingQuery.workinghours_type.length +
        store.getters.workingQuery.employment_place.length +
        (store.getters.isAdmin ? store.getters.workingQuery.flag.length : 0) +
        (store.getters.workingQuery.jobage ? 1 : 0) +
        store.getters.workingQuery.jobtitleid.length +
        numberOfActiveGeoFilters.value +
        numberOfActiveCategoryFilters.value
    );
});

const jobsearchSearchMobile = ref();

async function submit(closeOverlay = true) {
    if (userInput.value) {
        store.dispatch('addParam', { key: 'q', value: userInput.value });
        userInput.value = '';
    }
    store.commit('setParam', { key: 'page', value: 1 });
    if (closeOverlay){
        jobsearchSearchMobile.value?.close();
    }
    return store.dispatch('performSearch');
}

function clearAllFilters(){
    allFiltersComponent.value?.clearLocalValues();
    store.commit('clearParams');
}

function clearGeographyFilters(){
    geoFilter.value?.clearLocalValues();
    store.commit('clearGeoParams');
}

function clearCategoryFilters(){
    store.commit('clearCategoryParams');
}

function resetSearch() {
    userInput.value = '';
    store.commit('clearParams');
    submit(false);
}

</script>

<template>
    <section class="jobsearch-top-section">
        <div class="container">
            <form
                class="d-flex"
                @submit.prevent="submit"
                role="search"
                autocomplete="off"
            >
                <div class="jobsearch-input">
                    <!-- Main search field -->
                    <div class="jobsearch-input__main-search-field">
                        <JobsearchSearchMobile
                            ref="jobsearchSearchMobile"
                            v-if="isMobile"
                            :input-classes="'form-control jobsearch-input__hidden-background'"
                            @search="submit"
                            @reset-search="resetSearch"
                        />
                        <JobsearchSearchBarGeneral
                            v-else
                            ref="jobsearchbar"
                            :input-classes="'form-control jobsearch-input__hidden-background'"
                            :auto-focus="true"
                            @suggestion-selected="() => { userInput = ''; submit(); jobsearchbar.showDropdown = false; }"
                        />
                    </div>
                    <!-- Geography filter -->
                    <JobsearchFilterGroup
                        v-if="isMedium"
                        @reset="clearGeographyFilters"
                        @submit="submit"
                        :submit-button-label="jobcountText"
                    >
                        <template #button>
                            <SvgIcon
                                name="geolocation"
                                size="sm"
                            />
                            <div class="jobsearch-input__filter-button__text">
                                {{ __('Geografi') }}
                            </div>
                            <div class="round-filter-counter" v-if="numberOfActiveGeoFilters != 0">
                                {{ numberOfActiveGeoFilters }}
                            </div>
                        </template>
                        <template #content>
                            <JobsearchFilterGroupFilters ref="geoFilter" :only-show-geography="true" />
                        </template>
                    </JobsearchFilterGroup>
                    <!-- Categories filter -->
                    <JobsearchFilterGroup
                        v-if="isLarge"
                        @reset="clearCategoryFilters"
                        @submit="submit"
                        :submit-button-label="jobcountText"
                    >
                        <template #button>
                            <SvgIcon
                                name="category"
                                size="sm"
                            />
                            <div class="jobsearch-input__filter-button__text">
                                {{ __('Kategorier') }}
                            </div>
                            <div class="round-filter-counter" v-if="numberOfActiveCategoryFilters != 0">
                                {{ numberOfActiveCategoryFilters }}
                            </div>
                        </template>
                        <template #content>
                            <JobsearchFilterGroupFilters :only-show-categories="true" />
                        </template>
                    </JobsearchFilterGroup>
                    <!-- All filters -->
                    <JobsearchFilterGroup
                        @reset="clearAllFilters"
                        @submit="submit"
                        :submit-button-label="jobcountText"
                        :enable-fullscreen-overlay="isMobile"
                        ref="allFilters"
                        enable-max-height
                    >
                        <template #button v-if="!isMobile">
                            <SvgIcon
                                name="filter"
                                size="sm"
                            />
                            {{ __('Filtre') }}
                            <div class="round-filter-counter" v-if="numberOfActiveFilters != 0">
                                {{ numberOfActiveFilters }}
                            </div>
                        </template>
                        <template #content>
                            <JobsearchFilterGroupFilters ref="allFiltersComponent" />
                        </template>
                    </JobsearchFilterGroup>
                </div>
                <!-- All filters (mobile) -->
                <div class="position-relative ml-3" v-if="isMobile && !jobsearchbar?.showDropdown">
                    <DsIconButton
                        icon-name="filter"
                        type="primary"
                        class="jobsearch-round-filter-button"
                        :label="__('Filtre')"
                        @click.prevent="allFilters.toggle()"
                    />
                    <div class="round-filter-counter" v-if="numberOfActiveFilters != 0">
                        {{ numberOfActiveFilters }}
                    </div>
                </div>
            </form>
            <JobsearchRenderQuery
                class="mt-3"
                @query-parameter-removed="submit"
                :query-type="'searchQuery'"
                show-reset-search-button
                @reset-search="resetSearch"
            />
        </div>
    </section>
</template>
